// =============================================================================
// HOME PAGE COMPONENT
// =============================================================================

import React from 'react';
import { Footer } from '@/components/Layout';
import { ContentRenderer } from '@/components/ContentRenderer';
import { SEOHead } from '@/components/SEO';
import { LoadingPage, ErrorPage } from '@/components/LoadingPage';
import { FloatingShoppingBagIcon } from '@/components/ShoppingBag';
import { usePageData } from '@/hooks/usePageData';
import { useAnalytics } from '@/hooks/useAnalytics';
import { seoData } from '@/data/seoData';

export const HomePage: React.FC = () => {
  // Analytics tracking with automatic page views, scroll depth, and time on page
  useAnalytics('Awani - Luxury Alpaca Wool Clothing');
  
  // Load page data with caching and Firebase integration
  const { data, loading, error, reload } = usePageData('home', {
    preload: ['about', 'services'], // Preload likely next pages
  });

  // Show loading state
  if (loading) {
    return <LoadingPage message="Loading homepage..." />;
  }

  // Show error state with retry
  if (error) {
    return <ErrorPage error={error} onRetry={reload} />;
  }

  // No data available
  if (!data) {
    return <ErrorPage error={new Error('No data available')} onRetry={reload} />;
  }

  const seo = data.seo || seoData.home;
  const navItems = data.navigation?.navItems || [];
  const footerLinks = data.navigation?.footerLinks || [];
  const socialLinks = data.navigation?.socialLinks || [];
  
  // Handle SEO data safely
  const seoTitle = typeof seo.title === 'string' ? seo.title : seo.title;
  const seoKeywords = Array.isArray(seo.keywords) ? seo.keywords.join(', ') : seo.keywords;
  const seoCanonical = 'canonicalUrl' in seo ? (seo as any).canonicalUrl : undefined;
  const seoStructured = 'structuredData' in seo ? (seo as any).structuredData : undefined;
  
  return (
    <div className="home-page">
      <SEOHead
        title={seoTitle}
        description={seo.description}
        keywords={seoKeywords}
        canonicalUrl={seoCanonical}
        structuredData={seoStructured}
      />
      
      {/* Floating Shopping Bag Icon */}
      <FloatingShoppingBagIcon />
      
      <main className="main-content">
        <ContentRenderer components={data.components} />
      </main>
      
      <Footer
        brand="Awani"
        description="Luxury alpaca wool clothing made from organic fibers. Handcrafted by Peruvian artisans in the Sacred Valley. Sustainable high-end fashion."
        links={footerLinks}
        socialLinks={socialLinks}
        collapsible={true}
        defaultCollapsed={true}
      />
    </div>
  );
};
